import clsx from 'clsx';
import { useState } from 'react';

import { Loader } from '@/components/loader/loader';
import { Heading } from '@/components/typography/heading';
import { useResponseLogsQuery } from '@/hooks/queries/useLogs';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { RequestLog } from '@/types/logs';
import { truncate } from '@/utils/stringUtils';
import { formatISODateTime } from '@/utils/timeFormatter';

import ResponseLogDetailView from '../logDetail/responseLogDetailView';
import styles from './modelLogViewer.module.scss';

type Props = {
  agentId: string;
  requestId?: string;
  correlationId?: string;
  data: RequestLog[];
  initialSelectedId?: string;
  onSelectionChange?: ( id: string ) => void;
};

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const HashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="9" x2="20" y2="9"></line>
    <line x1="4" y1="15" x2="20" y2="15"></line>
    <line x1="10" y1="3" x2="8" y2="21"></line>
    <line x1="16" y1="3" x2="14" y2="21"></line>
  </svg>
);

const UserIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

function RequestResponseLogViewer( props: Props ) {
  const [ selectedId, setSelectedId ] = useState<string | null>( props.initialSelectedId ?? null );
  const selectedRequest = props.data?.find( r => r.id === selectedId ) ?? null;
  const callId = selectedRequest
    ? ( selectedRequest.meta_data as Record<string, unknown> )?.call_id as string ?? null
    : null;
  const [ useLocalTimeZone ] = useLocalZonePreference();

  const { data: responseData, isLoading: isLoadingResponses } = useResponseLogsQuery( callId );
  const responses = responseData?.completion_responses ?? [];

  const formatDateTime = ( dateString: string | undefined ) => formatISODateTime( dateString, useLocalTimeZone );

  const handleRequestSelect = ( request: RequestLog ) => {
    setSelectedId( request.id );
    props.onSelectionChange?.( request.id );
  };

  return (
    <div className={ styles.container }>
      {/* Left Pane - Request List */}
      <div className={ styles.leftPane }>
        <div className={ styles.leftPaneHeader }>
          <Heading as="h2" size="md" className={ styles.leftPaneTitle }>Requests</Heading>
          <p className={ styles.leftPaneSubtitle }>
            {props.data?.length || 0} requests
          </p>
        </div>

        <div>
          {props.data?.map( ( request, index ) => (
            <div
              key={ request.id || index }
              onClick={ () => handleRequestSelect( request ) }
              className={ clsx( styles.logItem, selectedId === request.id && styles.logItemSelected ) }
            >
              <div className={ styles.logId }>
                <HashIcon />
                <span>{truncate( request.request_id ?? request.id, 30 )}</span>
              </div>

              <div className={ styles.logDetail }>
                <ClockIcon />
                <span>{formatDateTime( request.start_time )}</span>
              </div>

              {request.method && request.path && (
                <div className={ styles.logDetail }>
                  <span className={ styles.logMethodPath }>
                    {request.method} {truncate( request.path, 30 )}
                  </span>
                </div>
              )}

              {request.agent_name && (
                <div className={ styles.logDetail }>
                  <UserIcon />
                  <span>{truncate( request.agent_name, 30 )}</span>
                </div>
              )}

              <div className={ styles.logBadgeContainer }>
                {request.status_code !== undefined && (
                  <span className={ request.status_code >= 400 ? styles.errorBadge : styles.successBadge }>
                    {request.status_code}
                  </span>
                )}
                {request.correlation_id && (
                  <span className={ styles.logCorrelationId }>
                    {truncate( request.correlation_id, 30 )}
                  </span>
                )}
              </div>
            </div>
          ) )}

          {( !props.data || props.data.length === 0 ) && (
            <div className={ styles.emptyMessage }>No requests to display</div>
          )}
        </div>
      </div>

      {/* Right Pane - Response Detail */}
      <div className={ styles.rightPane }>
        {isLoadingResponses ? (
          <div className={ styles.loaderContainer }>
            <Loader />
          </div>
        ) : selectedRequest ? (
          <div className={ styles.rightPaneContent }>
            <ResponseLogDetailView
              request={ selectedRequest }
              responses={ responses }
            />
          </div>
        ) : (
          <div className={ styles.emptyState }>
            <div className={ styles.emptyStateContent }>
              <div className={ styles.emptyStateIcon }>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <p>Select a request to view responses</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RequestResponseLogViewer;
